import { useRef, useState } from 'react';
import { getScopeRole } from '@/services/current-context';
import { ChevronDown, ChevronUp, LogOut, ArrowLeftRight } from 'lucide-react';

import { endSession } from '@/services/auth-token';
import { initiateOrgSwitch, orgSwitchLabel } from '@/services/org-data';
import { useNavigationService } from '@/services/navigate-service';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useOnClickOutside } from '@/components/ui/hooks/use-on-click-outside';
import { cn } from '@/lib/utils';
import { isInternalAdmin } from '@/util/roles';
import { useSelectedOrgName } from '@/hooks/useEntityScope';

/* ------------------------------------------------------------------ */
/*  UserAvatar (inlined - was src/ui/header/userAvatar/UserAvatar.tsx) */
/* ------------------------------------------------------------------ */
// The avatar shrinks in `compact` (mobile) headers so the right-hand cluster
// reads as chrome rather than crowding a 375px bar. Only the glyph shrinks --
// the button around it keeps its 44px touch target.
const AVATAR_SIZE_CLASSES = {
  default: 'h-10 w-10 text-xl',
  compact: 'h-8 w-8 text-base'
} as const;

function UserAvatar({
  image,
  size = 'default'
}: {
  image?: string;
  size?: keyof typeof AVATAR_SIZE_CLASSES;
}) {
  const userName = localStorage.getItem('userName');

  if (image) {
    return (
      <img
        src={image}
        alt="User Avatar"
        className={cn(
          'rounded-full object-cover',
          AVATAR_SIZE_CLASSES[size]
        )}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full bg-muted font-medium text-foreground',
        AVATAR_SIZE_CLASSES[size]
      )}
    >
      {userName?.charAt(0)}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  UserInfo (inlined - was src/ui/header/userInfo/UserInfo.tsx)       */
/* ------------------------------------------------------------------ */
function UserInfo() {
  const { navigateTo } = useNavigationService();
  const OrgData: string | null = localStorage.getItem('OrgData');
  const companiesAndRoles = OrgData ? JSON.parse(OrgData) : null;

  const navigateToOrg = () => navigateTo(initiateOrgSwitch());

  return (
    <div
      className={cn(
        'w-[278px] min-h-[50px] bg-popover rounded shadow-popover border border-border',
        companiesAndRoles?.length > 1 && 'min-h-[90px]'
      )}
    >
      <div className="flex flex-col">
        {companiesAndRoles?.length > 1 && (
          <div
            className={cn(
              'text-center py-1',
              companiesAndRoles?.length > 1 && 'border-b border-border'
            )}
          >
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground hover:bg-accent"
              onClick={navigateToOrg}
            >
              <ArrowLeftRight className="h-5 w-5 mr-2 text-foreground" />
              {orgSwitchLabel(
                isInternalAdmin(getScopeRole())
                  ? 'Switch Account'
                  : 'Switch Organization'
              )}
            </Button>
          </div>
        )}
        <div
          className={cn(
            'text-center py-1',
            companiesAndRoles?.length > 1 && 'border-b border-border'
          )}
        >
          <Button
            variant="ghost"
            className="w-full justify-start text-foreground hover:bg-accent"
            onClick={endSession}
          >
            <LogOut className="h-5 w-5 mr-2 text-foreground" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Avatar + org/user identity + switch-account/sign-out dropdown, shared by
 * the desktop and mobile headers. `compact` renders the avatar alone
 * (mobile: the org/name text is redundant at 375px) at the 44px tap floor.
 */
export function UserMenu({ compact = false }: { compact?: boolean }) {
  // The org label follows the scope source, so it can never name a different
  // account than the app is scoped to (DV-3013 §2.1 class B).
  const selectedOrgName = useSelectedOrgName();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(dropdownRef, () => setDropdownOpen(false));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={cn(
          'flex items-center gap-2 border-none bg-transparent cursor-pointer px-1',
          compact && 'min-h-11 min-w-11 justify-center'
        )}
        onClick={() => setDropdownOpen((prev) => !prev)}
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <UserAvatar size={compact ? 'compact' : 'default'} />
        {!compact && (
          <div className="flex flex-col text-left">
            <span className="text-sm font-semibold text-foreground whitespace-nowrap w-[102px] overflow-hidden text-ellipsis">
              {selectedOrgName}
            </span>
            <Typography variant="small" className="text-muted-foreground">
              {`${localStorage.getItem('userName')}`}
            </Typography>
          </div>
        )}
        {!compact &&
          (dropdownOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ))}
      </button>
      {dropdownOpen && (
        <div className="absolute right-0 top-full mt-1 z-50">
          <UserInfo />
        </div>
      )}
    </div>
  );
}
